<template lang="pug">
.Loader
  canvas.fireworks
  p.look-further {{$t('look_further')}}
  p.loading(:style="{ fontVariationSettings: `'wght' ${fontWeight}, 'wdth' 200` }"): span(v-for="n of Math.round(lerp(dates.from, dates.to, loadingProgress)).toString().split('')") {{ n }}
  button.start(@click="start")
    svg(width="22px", height="18px")
      use(xlink:href="../assets/images/arrow.svg#arrow")
  p.help {{$t('navigate_with_cursor')}}
</template>

<script>
import gsap from 'gsap'
import { lerp } from '@/js/utils'

class Particule {
  constructor (position, direction, speed) {
    this.speed = speed

    this.position = position
    this.velocity = { x: 0, y: 0 }
    this.acceleration = { x: Math.cos(direction) * speed, y: -Math.sin(direction) * speed }
    this.alive = true
  }

  kill () { this.alive = false }

  update () {
    if (!this.alive) return
    this.velocity = { x: this.velocity.x + this.acceleration.x, y: this.velocity.y + this.acceleration.y }
    this.position = { x: this.position.x + this.velocity.x, y: this.position.y + this.velocity.y }
    this.acceleration = { x: 0, y: 0.1 }
  }
}

export default {
  name: 'loader',
  data () {
    return {
      lerp,
      loadingProgress: 0,
      dates: { from: 2005, to: 2020 },
      canvas: {},
      ctx: {},
      particules: Array(60).fill({}),
      particulesPlay: false,
      interval: {},
      fontWeight: 100
    }
  },
  methods: {
    start () {
      gsap.timeline({ onComplete: () => this.$store.commit('loaderDone') })
        .to(this.$el.querySelector('.loading'), { autoAlpha: 0, y: 30, duration: 1.5, ease: 'expo.out' }, 0.5)
        .to(this.$el.querySelector('.start'), { autoAlpha: 0, scale: 0, duration: 1, ease: 'expo.inOut' }, 0)
        .add(() => this.$store.commit('loaderDone'), 0.1)
        .to(this.$el, { y: '-100%', duration: 1.5, ease: 'expo.inOut' }, 0)
        .set(this.$el, { display: 'none' }, 1.5)
    },
    animate () {
      if (this.$store.state.loaderIsVisible) requestAnimationFrame(this.animate)

      this.fontWeight = lerp(this.fontWeight, lerp(100, 700, this.loadingProgress), 0.1)

      if (this.particulesPlay && this.particules.filter(p => p.alive).length > 0) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.ctx.strokeStyle = '#fff'

        this.particules = this.particules.map(particule => {
          if (particule.position.x > this.canvas.width || particule.position.x < 0 || particule.position.y > this.canvas.height) {
            particule.kill()
            return particule // new Particule({ x: this.canvas.width / 2, y: this.canvas.height * 0.75 }, Math.random() * Math.PI * 2, Math.random() * 10)
          }

          this.ctx.lineWidth = 1 + (10 - particule.speed) / 3
          particule.update()
          this.ctx.beginPath()
          this.ctx.moveTo(particule.position.x, particule.position.y)
          this.ctx.lineTo(particule.position.x - particule.velocity.x * 2, particule.position.y - particule.velocity.y * 2)
          this.ctx.stroke()

          return particule
        })
      }
    },
    canvasSetup () {
      this.canvas = this.$el.querySelector('canvas.fireworks')
      this.canvas.width = window.innerWidth
      this.canvas.height = window.innerHeight
      this.ctx = this.canvas.getContext('2d')

      this.particules = this.particules.map(p => new Particule({ x: this.canvas.width / 2, y: this.canvas.height * 0.5 }, Math.random() * Math.PI * 2, Math.random() * 10))
    }
  },
  mounted () {
    this.canvasSetup()
    requestAnimationFrame(this.animate)

    const tl = gsap.timeline({ onComplete: () => { this.particulesPlay = true }, paused: true })
      .to(this.$el.querySelector('.help'), { autoAlpha: 0, duration: 1, ease: 'expo.inOut' })
      .from(this.$el.querySelector('.start'), { scale: 0, duration: 0.5, ease: 'expo.out' })

    this.interval = window.setInterval(() => {
      if (this.loadingProgress < 1) this.loadingProgress = Math.min(this.loadingProgress + Math.random() / 10, 1)
      else {
        window.clearInterval(this.interval)
        tl.play()
      }
    }, 50)
  }
}
</script>

<style lang="scss" scoped>
.Loader {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  background-color: $black;
  z-index: 50;
  color: $white;

  .fireworks {
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    mix-blend-mode: difference;
  }

  .look-further {
    position: absolute;
    bottom: 80vh;
    text-align: center;
    left: 0;
    right: 0;
    text-align: center;
    font-family: $font-rz;
    font-size: 16px;
    margin: 0;
  }

  .loading {
    font-size: 15vw;
    font-family: $font-v;
    font-weight: 800;
    text-align: center;
    line-height: 100vh;
    margin: 0;

    @media (min-width: 1440px) {
      font-size: 220px;
    }

    span {
      width: 1em;
      display: inline-block;
    }
  }

  .help {
    position: absolute;
    top: 70vh;
    left: calc(50vw - 100px);
    width: 200px;
    text-align: center;
    opacity: .5;
    font-family: $font-rz;
    margin: 0;
  }

  .start {
    position: absolute;
    top: 70vh;
    left: calc(50vw - 45px);
    width: 90px;
    height: 90px;
    border-radius: 45px;
    border: 2px solid rgba($white, 0.5);
    background-color: $white;
    color: $black;
    text-align: center;
    font-family: $font-rz;
    font-size: 1.5rem;
    cursor: pointer;
    transition: background-color .1s ease-out, color .1s ease-out;

    &:hover {
      background-color: $black;
      color: $white;
    }

    svg {
      position: relative;
      top: 2px;
      left: 2px;
    }
  }
}
</style>
